import { action, computed, observable, makeAutoObservable } from 'mobx';
import { TopEventsEventsState, CompetitionCombinedType } from 'src/AppState/CmsState/TopEventsState/TopEventsState';
import { TrpcClient } from 'src/AppState/TrpcClient';

export class TopEventsEventCompetitionState {
    @observable public isOpenSection: boolean = false;
    @observable public isActive: boolean = false;
    @observable public showDeletePopup: boolean = false;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly competitionId: number,
        private readonly getCompetitionData: (competitionId: number) => CompetitionCombinedType | null,
        private readonly handleDeleteSport: () => Promise<void>,
        private readonly topEventsEventsSectionState: TopEventsEventsState
    ) {
        this.isActive = this.getCompetitionData(this.competitionId)?.is_active ?? false;
        makeAutoObservable(this);
    }

    @computed private get competitionData(): CompetitionCombinedType | null {
        return this.getCompetitionData(this.competitionId) ?? null;
    }

    @action public setCompetitionActive = async (): Promise<void> => {
        if (this.competitionData === null) {
            console.error('Cannot find the competition with provided ID.');
            return;
        }

        const topEventsCompetitionDetailsData = {
            ...this.competitionData,
            is_active: !this.competitionData.is_active,
        };

        this.isActive = !this.isActive;

        try {
            await this.trpcClient.client.topEventsCompetitions.updateTopEventsCompetition.mutate({
                data: topEventsCompetitionDetailsData,
                id: this.competitionData.id,
            });

            await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
        } catch (error) {
            console.error(error);
        }
    };

    @action public handleToggleOpenSection = (): void => {
        this.isOpenSection = !this.isOpenSection;
    };

    @action public handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };

    @action public handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleDeleteCompetition = async (): Promise<void> => {
        if (this.competitionData !== null) {
            const sportSlug = this.competitionData.sport_id;
            const removeResult = await this.trpcClient.client.topEventsCompetitions.deleteTopEventsCompetition.mutate({
                id: this.competitionData.id,
            });

            if (removeResult.status === 200) {
                const eventIds = this.competitionData.events_list.map((e) => e.id);
                await this.handleDeleteAllEvents(eventIds);
                await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
                this.topEventsEventsSectionState.setSportsDataFromResource();

                const competitionsList = this.topEventsEventsSectionState.getSportData(sportSlug)?.competitions_list;
                if (competitionsList?.length === 0) {
                    await this.handleDeleteSport();
                }
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };

    @action public handleDeleteAllEvents = async (eventIds: Array<number>): Promise<void> => {
        for (const id of eventIds) {
            const removeResult = await this.trpcClient.client.topEventsEvents.deleteTopEventsEvent.mutate({
                id,
            });

            if (removeResult.status === 200) {
                await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
            } else {
                console.error("The item couldn't be deleted.");
            }
        }
    };
}
